/**
 * Authentication Hooks
 * Custom React hooks for authentication functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth as useAuthContext } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import {
  User,
  LoginCredentials,
  RegisterData,
  ProfileUpdateData,
  PasswordChangeData,
  UserRole,
  Permission,
} from '../types/auth';

// Main authentication hook (re-export from context)
export const useAuth = () => {
  return useAuthContext();
};

// Hook for handling login form
export const useLogin = () => {
  const { login, error, isLoading, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setIsSubmitting(true);
      clearError();

      try {
        const user = await login(credentials);
        // Return success with user info for role-based redirect
        return { success: true, user };
      } catch (error: any) {
        // NOTE: axios interceptor (api.ts) transforms errors to standardError with .details property
        // Priority: error.details (from interceptor) > error.message (fallback)
        const errorMessage =
          error.details ||
          error.response?.data?.error?.details ||
          error.response?.data?.detail ||
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Login failed';

        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, clearError]
  );

  return {
    handleLogin,
    isSubmitting,
    error,
    isLoading,
    clearError,
  };
};

// Hook for handling registration form
export const useRegister = () => {
  const { register, error, isLoading, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleRegister = useCallback(
    async (data: RegisterData) => {
      setIsSubmitting(true);
      setRegistrationSuccess(false);
      clearError();

      try {
        const user = await register(data);
        setRegistrationSuccess(true);
        // Return success with user info for role-based redirect
        return { success: true, user };
      } catch (error: any) {
        // NOTE: axios interceptor (api.ts) transforms errors to standardError with .details property
        // Priority: error.details (from interceptor) > error.message (fallback)
        const errorMessage =
          error.details ||
          error.response?.data?.error?.details ||
          error.response?.data?.detail ||
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Registration failed';

        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsSubmitting(false);
      }
    },
    [register, clearError]
  );

  const resetRegistration = useCallback(() => {
    setRegistrationSuccess(false);
    clearError();
  }, [clearError]);

  return {
    handleRegister,
    isSubmitting,
    registrationSuccess,
    error,
    isLoading,
    clearError,
    resetRegistration,
  };
};

// Hook for profile management
export const useProfile = () => {
  const { user, updateProfile, error, clearError } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const handleUpdateProfile = useCallback(
    async (data: ProfileUpdateData) => {
      setIsUpdating(true);
      setUpdateSuccess(false);
      clearError();

      try {
        await updateProfile(data);
        setUpdateSuccess(true);
        return { success: true };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || 'Profile update failed',
        };
      } finally {
        setIsUpdating(false);
      }
    },
    [updateProfile, clearError]
  );

  const resetUpdateStatus = useCallback(() => {
    setUpdateSuccess(false);
    clearError();
  }, [clearError]);

  return {
    user,
    handleUpdateProfile,
    isUpdating,
    updateSuccess,
    error,
    clearError,
    resetUpdateStatus,
  };
};

// Hook for password management
export const usePasswordChange = () => {
  const { changePassword, error, clearError } = useAuth();
  const [isChanging, setIsChanging] = useState(false);
  const [changeSuccess, setChangeSuccess] = useState(false);

  const handlePasswordChange = useCallback(
    async (data: PasswordChangeData) => {
      setIsChanging(true);
      setChangeSuccess(false);
      clearError();

      try {
        await changePassword(data.currentPassword, data.newPassword);
        setChangeSuccess(true);
        return { success: true };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || 'Password change failed',
        };
      } finally {
        setIsChanging(false);
      }
    },
    [changePassword, clearError]
  );

  const resetChangeStatus = useCallback(() => {
    setChangeSuccess(false);
    clearError();
  }, [clearError]);

  return {
    handlePasswordChange,
    isChanging,
    changeSuccess,
    error,
    clearError,
    resetChangeStatus,
  };
};

// Hook for password reset
export const usePasswordReset = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const requestReset = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);
    setResetSent(false);

    try {
      await authService.requestPasswordReset(email);
      setResetSent(true);
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Password reset request failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const confirmReset = useCallback(async (token: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);
    setResetSuccess(false);

    try {
      await authService.confirmPasswordReset({
        token,
        newPassword,
        confirmNewPassword: newPassword,
      });
      setResetSuccess(true);
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Password reset failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetState = useCallback(() => {
    setResetSent(false);
    setResetSuccess(false);
    setError(null);
  }, []);

  return {
    requestReset,
    confirmReset,
    isLoading,
    error,
    resetSent,
    resetSuccess,
    clearError,
    resetState,
  };
};

// Hook for email verification
// NOTE: Email verification removed - all users can login immediately
export const useEmailVerification = () => {
  const [isLoading] = useState(false);
  const [error] = useState<string | null>(null);
  const [verificationSuccess] = useState(false);

  const verifyEmail = useCallback(async (_token: string) => {
    // Email verification removed - return success immediately
    return { success: true };
  }, []);

  const resendVerification = useCallback(async () => {
    // Email verification removed - return success immediately
    return { success: true };
  }, []);

  const clearError = useCallback(() => {
    // No-op since email verification is removed
  }, []);

  return {
    verifyEmail,
    resendVerification,
    isLoading,
    error,
    verificationSuccess,
    clearError,
  };
};

// Hook for checking permissions
export const usePermissions = () => {
  const { user, hasRole, hasPermission, canManageCompanies, isAdmin } = useAuth();

  const checkRole = useCallback(
    (role: UserRole): boolean => {
      return hasRole(role);
    },
    [hasRole]
  );

  const checkPermission = useCallback(
    (permission: Permission): boolean => {
      return hasPermission(permission);
    },
    [hasPermission]
  );

  const checkRoles = useCallback(
    (roles: UserRole[]): boolean => {
      return roles.some(role => hasRole(role));
    },
    [hasRole]
  );

  const checkPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every(permission => hasPermission(permission));
    },
    [hasPermission]
  );

  return {
    user,
    checkRole,
    checkRoles,
    checkPermission,
    checkPermissions,
    canManageCompanies,
    isAdmin,
  };
};

// Hook for user session management
export const useSession = () => {
  const { user, isAuthenticated, token, refreshToken, logout } = useAuth();
  const [sessionInfo, setSessionInfo] = useState<{
    expiresIn: number;
    isExpiringSoon: boolean;
  } | null>(null);

  useEffect(() => {
    if (token) {
      try {
        // Decode JWT token to get expiration
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;
        const expiresIn = payload.exp - now;
        const isExpiringSoon = expiresIn < 5 * 60; // Less than 5 minutes

        setSessionInfo({
          expiresIn: Math.max(0, expiresIn),
          isExpiringSoon,
        });
      } catch (error) {
        console.warn('Failed to decode JWT token:', error);
      }
    } else {
      setSessionInfo(null);
    }
  }, [token]);

  const extendSession = useCallback(async () => {
    try {
      await refreshToken();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to extend session' };
    }
  }, [refreshToken]);

  const endSession = useCallback(() => {
    logout();
  }, [logout]);

  return {
    user,
    isAuthenticated,
    sessionInfo,
    extendSession,
    endSession,
  };
};

// Hook for form validation
export const useFormValidation = <T extends Record<string, any>>(
  initialValues: T,
  validate: (values: T) => Partial<Record<keyof T, string>>
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = useCallback(
    (name: keyof T, value: any) => {
      const fieldErrors = validate({ ...values, [name]: value });
      return fieldErrors[name] || null;
    },
    [values, validate]
  );

  const handleChange = useCallback(
    (name: keyof T) => (value: any) => {
      setValues(prev => ({ ...prev, [name]: value }));

      if (touched[name]) {
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    },
    [touched, validateField]
  );

  const handleBlur = useCallback(
    (name: keyof T) => () => {
      setTouched(prev => ({ ...prev, [name]: true }));
      const error = validateField(name, values[name]);
      setErrors(prev => ({ ...prev, [name]: error }));
    },
    [values, validateField]
  );

  const validateAll = useCallback(() => {
    const allErrors = validate(values);
    setErrors(allErrors);
    setTouched(
      Object.keys(values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    );
    return Object.keys(allErrors).length === 0;
  }, [values, validate]);

  const handleSubmit = useCallback(
    (onSubmit: (values: T) => Promise<any>) => async (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      setIsSubmitting(true);
      const isValid = validateAll();

      if (isValid) {
        try {
          await onSubmit(values);
        } catch (error) {
          console.error('Form submission error:', error);
        }
      }

      setIsSubmitting(false);
    },
    [values, validateAll]
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    validateAll,
    reset,
  };
};

// Hook for async data fetching with authentication
export const useAuthenticatedFetch = <T>(
  fetchFn: () => Promise<T>,
  dependencies: any[] = []
) => {
  const { isAuthenticated, token } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      setData(result);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Fetch failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token, fetchFn]);

  useEffect(() => {
    fetch();
  }, [fetch, ...dependencies]);

  const refetch = useCallback(() => {
    fetch();
  }, [fetch]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};